import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Chip } from '@mui/material';
import { Upload, Add, Edit, Delete, CheckCircle, RadioButtonUnchecked, Receipt, EmojiEvents } from '@mui/icons-material';
import { saveAs } from 'file-saver';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { createStudent, updateStudentAsync, deleteStudentAsync } from '../../store/slices/studentSlice';
import schoolService from '../../services/schoolService';
import studentsService from '../../services/studentService';
import { Student, School, ImportResult } from '../../types';
import ImportDialog from '../../components/ImportDialog';

const SubjectChip: React.FC<{ subject: string; isSubscribed: boolean }> = ({ subject, isSubscribed }) => (
  <Chip
    label={subject}
    size="small"
    variant={isSubscribed ? "filled" : "outlined"}
    color={isSubscribed ? "success" : "default"}
    icon={isSubscribed ? <CheckCircle fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
    sx={{
      m: 0.5,
      fontSize: '0.75rem',
      height: 24,
      minWidth: 80,
      '& .MuiChip-icon': {
        color: isSubscribed ? 'white' : 'text.secondary',
      },
    }}
  />
);

const SchoolStudentsPage: React.FC = () => {
  const { schoolId } = useParams<{ schoolId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [school, setSchool] = useState<School | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [openStudentDialog, setOpenStudentDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    securityPin: '',
    grade: '',
    section: '',
    rollNumber: '',
    userId: '',
    subjects: [] as string[],
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const schoolRes = await schoolService.getSchoolById(schoolId!);
        setSchool(schoolRes.data);
        // Students are returned sorted by class (grade) → section → name from the backend
        const studentsRes = await studentsService.getStudentsBySchool(schoolId!);
        setStudents(studentsRes.data);
      } catch (err: any) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [schoolId]);

  const handleExportStudents = async () => {
    if (!schoolId || !school) return;
    setExporting(true);
    setExportError(null);
    try {
      const blob = await studentsService.exportStudentsBySchool(schoolId);
      saveAs(blob, `${school.name}.xlsx`);
    } catch (err: any) {
      setExportError(err.message || 'Failed to export students');
    } finally {
      setExporting(false);
    }
  };

  const handleValidateImport = async (file: File): Promise<ImportResult> => {
    try {
      const response = await studentsService.validateImport(file);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const handleExecuteImport = async (file: File): Promise<ImportResult> => {
    try {
      const response = await studentsService.executeImport(file);
      // Refresh students list after successful import
      if (response.data.success) {
        const studentsRes = await studentsService.getStudentsBySchool(schoolId!);
        setStudents(studentsRes.data);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const handleOpenStudentDialog = (student?: Student) => {
    if (student) {
      setEditingStudent(student);
      setFormData({
        fullName: student.fullName || student.name || '',
        email: student.email || '',
        phoneNumber: student.phoneNumber || student.phone || '',
        securityPin: student.securityPin || '',
        grade: student.grade || '',
        section: student.section || '',
        rollNumber: student.rollNumber || '',
        userId: student.userId || '',
        subjects: student.subjects || [],
      });
    } else {
      setEditingStudent(null);
      setFormData({
        fullName: '',
        email: '',
        phoneNumber: '',
        securityPin: '',
        grade: '',
        section: '',
        rollNumber: '',
        userId: '',
        subjects: [],
      });
    }
    setOpenStudentDialog(true);
  };

  const handleCloseStudentDialog = () => {
    setOpenStudentDialog(false);
    setEditingStudent(null);
    setFormData({
      fullName: '',
      email: '',
      phoneNumber: '',
      securityPin: '',
      grade: '',
      section: '',
      rollNumber: '',
      userId: '',
      subjects: [],
    });
  };

  const handleSubmitStudent = async () => {
    try {
      if (editingStudent) {
        await dispatch(updateStudentAsync({
          id: editingStudent.id,
          studentData: { ...formData, schoolId: schoolId! }
        })).unwrap();
      } else {
        await dispatch(createStudent({ ...formData, schoolId: schoolId! })).unwrap();
      }
      handleCloseStudentDialog();
      // Refresh students list
      const studentsRes = await studentsService.getStudentsBySchool(schoolId!);
      setStudents(studentsRes.data);
    } catch (error) {
      console.error('Failed to save student:', error);
    }
  };

  const handleDeleteStudent = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await dispatch(deleteStudentAsync(id)).unwrap();
        // Refresh students list
        const studentsRes = await studentsService.getStudentsBySchool(schoolId!);
        setStudents(studentsRes.data);
      } catch (error) {
        console.error('Failed to delete student:', error);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const allSubjects = ['Math', 'Science', 'English'];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>{school?.name || 'School Students'}</Typography>
          <Typography variant="body2" color="text.secondary">All students enrolled in this school</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => setOpenImportDialog(true)}
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              '&:hover': {
                borderColor: '#5a6fd8',
                backgroundColor: 'rgba(102, 126, 234, 0.04)',
              },
            }}
          >
            Import Students
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenStudentDialog()}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            Add Student
          </Button>
          <Button variant="outlined" onClick={handleExportStudents} disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export Students'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Receipt />}
            onClick={() => navigate(`/schools/${schoolId}/classes`, {
              state: {
                students: students,
                schoolName: school?.name
              }
            })}
            sx={{
              borderColor: '#f59e0b',
              color: '#f59e0b',
              '&:hover': {
                borderColor: '#d97706',
                backgroundColor: 'rgba(245, 158, 11, 0.04)',
              },
            }}
          >
            Hall Tickets
          </Button>
          <Button
            variant="outlined"
            startIcon={<EmojiEvents />}
            onClick={() => navigate(`/schools/${schoolId}/certificates`, {
              state: {
                schoolName: school?.name
              }
            })}
            sx={{
              borderColor: '#10b981',
              color: '#10b981',
              '&:hover': {
                borderColor: '#059669',
                backgroundColor: 'rgba(16, 185, 129, 0.04)',
              },
            }}
          >
            Certificates
          </Button>
        </Box>
      </Box>
      {exportError && <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>}
      <Card>
        <CardContent>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Security Pin</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subjects</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student) => (
                  <TableRow key={student.id} hover>
                    <TableCell>{student.fullName}</TableCell>
                    <TableCell>{student.grade || '-'}</TableCell>
                    <TableCell>{student.section || '-'}</TableCell>
                    <TableCell>{(student.phoneNumber || '-').replace(/^\+91/, '')}</TableCell>
                    <TableCell>
                      <Chip
                        label={student.securityPin || 'N/A'}
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {allSubjects.map((subject) => (
                          <SubjectChip
                            key={subject}
                            subject={subject}
                            isSubscribed={student.subjects?.includes(subject) || false}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenStudentDialog(student)}
                          sx={{ color: 'primary.main' }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteStudent(student.id)}
                          sx={{ color: 'error.main' }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <Button sx={{ mt: 3 }} onClick={() => navigate(-1)}>
        Back to Schools
      </Button>

      {/* Add/Edit Student Dialog */}
      <Dialog open={openStudentDialog} onClose={handleCloseStudentDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingStudent ? 'Edit Student' : 'Add New Student'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="User ID"
                value={formData.userId}
                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                sx={{ mb: 2 }}
                helperText="Parent's user ID"
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Security Pin"
                value={formData.securityPin}
                onChange={(e) => setFormData({ ...formData, securityPin: e.target.value })}
                sx={{ mb: 2 }}
                helperText="4-digit security code"
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Grade</InputLabel>
                <Select
                  value={formData.grade}
                  label="Grade"
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                >
                  <MenuItem value="Class-01">Class-01</MenuItem>
                  <MenuItem value="Class-02">Class-02</MenuItem>
                  <MenuItem value="Class-03">Class-03</MenuItem>
                  <MenuItem value="Class-04">Class-04</MenuItem>
                  <MenuItem value="Class-05">Class-05</MenuItem>
                  <MenuItem value="Class-06">Class-06</MenuItem>
                  <MenuItem value="Class-07">Class-07</MenuItem>
                  <MenuItem value="Class-08">Class-08</MenuItem>
                  <MenuItem value="Class-09">Class-09</MenuItem>
                  <MenuItem value="Class-10">Class-10</MenuItem>
                  <MenuItem value="Class-11">Class-11</MenuItem>
                  <MenuItem value="Class-12">Class-12</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Section"
                value={formData.section}
                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Roll Number"
                value={formData.rollNumber}
                onChange={(e) => setFormData({ ...formData, rollNumber: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Subjects</InputLabel>
                <Select
                  multiple
                  value={formData.subjects}
                  label="Subjects"
                  onChange={(e) => setFormData({ ...formData, subjects: e.target.value as string[] })}
                >
                  <MenuItem value="Math">Math</MenuItem>
                  <MenuItem value="Science">Science</MenuItem>
                  <MenuItem value="English">English</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStudentDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitStudent}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            {editingStudent ? 'Update' : 'Add'} Student
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <ImportDialog
        open={openImportDialog}
        onClose={() => setOpenImportDialog(false)}
        onValidate={handleValidateImport}
        onExecute={handleExecuteImport}
      />
    </Box>
  );
};

export default SchoolStudentsPage; 